import React, { useEffect, useState, createContext, useContext } from 'react';
import { supabase } from '../utils/supabase';
import { Patient, Doctor, Appointment, Service, Insurance, Specialty, Contact, Company, Opportunity, MedicalHistory, MedicalEntry, Interaction, AppointmentStatus, ClinicalHistory, Role, Permission } from '../types';
import { getToday } from '../utils/helpers';
import { emailNotificationService } from '../utils/emailNotifications';
import { getInitialData } from '../utils/initialData';
interface DataContextType {
  // Patients
  patients: Patient[];
  addPatient: (patient: Omit<Patient, 'id' | 'medicalHistory' | 'interactions' | 'createdAt'>) => Promise<Patient>;
  updatePatient: (id: string, data: Partial<Patient>) => Promise<void>;
  deletePatient: (id: string) => Promise<void>;
  getPatient: (id: string) => Patient | undefined;
  updateMedicalHistory: (patientId: string, history: Partial<MedicalHistory>) => Promise<void>;
  addMedicalEntry: (patientId: string, entry: Omit<MedicalEntry, 'id' | 'date'>) => Promise<void>;
  addPatientInteraction: (patientId: string, interaction: Omit<Interaction, 'id' | 'date'>) => Promise<void>;
  // Doctors
  doctors: Doctor[];
  addDoctor: (doctor: Omit<Doctor, 'id' | 'createdAt'>) => Promise<Doctor>;
  updateDoctor: (id: string, data: Partial<Doctor>) => Promise<void>;
  deleteDoctor: (id: string) => Promise<void>;
  getDoctor: (id: string) => Doctor | undefined;
  getDoctorsBySpecialty: (specialtyId: string) => Doctor[];
  // Appointments
  appointments: Appointment[];
  addAppointment: (appointment: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Appointment>;
  updateAppointment: (id: string, data: Partial<Appointment>) => Promise<void>;
  cancelAppointment: (id: string, reason: string) => Promise<void>;
  rescheduleAppointment: (id: string, newDate: string, newTime: string) => Promise<void>;
  completeAppointment: (id: string, nextAppointmentDate?: string, nextAppointmentNotes?: string) => Promise<void>;
  getAppointment: (id: string) => Appointment | undefined;
  getAppointmentsByDate: (date: string) => Appointment[];
  getAppointmentsByPatient: (patientId: string) => Appointment[];
  getAppointmentsByDoctor: (doctorId: string) => Appointment[];
  checkDoctorAvailability: (doctorId: string, date: string, time: string, excludeId?: string) => boolean;
  syncAllAppointmentsToSheets: () => Promise<void>;
  // Services
  services: Service[];
  addService: (service: Omit<Service, 'id'>) => Promise<Service>;
  updateService: (id: string, data: Partial<Service>) => Promise<void>;
  deleteService: (id: string) => Promise<void>;
  getService: (id: string) => Service | undefined;
  // Insurance
  insurances: Insurance[];
  addInsurance: (insurance: Omit<Insurance, 'id'>) => Promise<Insurance>;
  updateInsurance: (id: string, data: Partial<Insurance>) => Promise<void>;
  deleteInsurance: (id: string) => Promise<void>;
  getInsurance: (id: string) => Insurance | undefined;
  // Specialties
  specialties: Specialty[];
  addSpecialty: (specialty: Omit<Specialty, 'id'>) => Promise<Specialty>;
  updateSpecialty: (id: string, data: Partial<Specialty>) => Promise<void>;
  deleteSpecialty: (id: string) => Promise<void>;
  getSpecialty: (id: string) => Specialty | undefined;
  // Contacts
  contacts: Contact[];
  addContact: (contact: Omit<Contact, 'id' | 'interactions' | 'createdAt'>) => Promise<Contact>;
  updateContact: (id: string, data: Partial<Contact>) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;
  getContact: (id: string) => Contact | undefined;
  addContactInteraction: (contactId: string, interaction: Omit<Interaction, 'id' | 'date'>) => Promise<void>;
  // Companies
  companies: Company[];
  addCompany: (company: Omit<Company, 'id' | 'createdAt'>) => Promise<Company>;
  updateCompany: (id: string, data: Partial<Company>) => Promise<void>;
  deleteCompany: (id: string) => Promise<void>;
  getCompany: (id: string) => Company | undefined;
  // Opportunities
  opportunities: Opportunity[];
  addOpportunity: (opportunity: Omit<Opportunity, 'id' | 'interactions' | 'createdAt' | 'updatedAt'>) => Promise<Opportunity>;
  updateOpportunity: (id: string, data: Partial<Opportunity>) => Promise<void>;
  deleteOpportunity: (id: string) => Promise<void>;
  getOpportunity: (id: string) => Opportunity | undefined;
  addOpportunityInteraction: (opportunityId: string, interaction: Omit<Interaction, 'id' | 'date'>) => Promise<void>;
  // Clinical Histories
  clinicalHistories: ClinicalHistory[];
  addClinicalHistory: (history: Omit<ClinicalHistory, 'id' | 'createdAt' | 'updatedAt'>) => Promise<ClinicalHistory>;
  updateClinicalHistory: (id: string, data: Partial<ClinicalHistory>) => Promise<void>;
  deleteClinicalHistory: (id: string) => Promise<void>;
  getClinicalHistory: (id: string) => ClinicalHistory | undefined;
  getClinicalHistoriesByPatient: (patientId: string) => ClinicalHistory[];
  // Roles
  roles: Role[];
  addRole: (role: Omit<Role, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Role>;
  updateRole: (id: string, data: Partial<Role>) => Promise<void>;
  deleteRole: (id: string) => Promise<void>;
  getRole: (id: string) => Role | undefined;
  // Permissions
  permissions: Permission[];
  getPermissionsByRole: (roleId: string) => Permission[];
  updatePermissions: (roleId: string, permissions: Permission[]) => Promise<void>;
  // Stats
  getTodayAppointmentsCount: () => number;
  getTotalOpportunitiesAmount: () => number;
  // Loading state
  isLoading: boolean;
}
const DataContext = createContext<DataContextType | undefined>(undefined);
interface DataProviderProps {
  children: React.ReactNode;
}
export function DataProvider({
  children
}: DataProviderProps) {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [insurances, setInsurances] = useState<Insurance[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [clinicalHistories, setClinicalHistories] = useState<ClinicalHistory[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [useSupabase, setUseSupabase] = useState(false);
  // Check if Supabase is configured (simpler check)
  useEffect(() => {
    const checkSupabase = async () => {
      try {
        // Simply check if we can connect to Supabase
        const {
          error
        } = await supabase.from('specialties').select('count').limit(1);
        if (!error) {
          // Supabase is working, use it
          setUseSupabase(true);
          console.log('✅ Usando Supabase para datos');
        } else {
          setUseSupabase(false);
          console.log('ℹ️ Usando modo demo (localStorage) para datos.');
          console.log('💡 Tus datos están guardados en localStorage del navegador.');
        }
      } catch (e) {
        console.log('ℹ️ Usando modo demo (localStorage) para datos.');
        console.log('💡 Tus datos están guardados en localStorage del navegador.');
        setUseSupabase(false);
      }
    };
    checkSupabase();
  }, []);
  // Load all data on mount
  useEffect(() => {
    if (useSupabase !== null) {
      loadAllData();
    }
  }, [useSupabase]);
  // Save to localStorage when data changes (only in demo mode)
  useEffect(() => {
    if (!useSupabase && !isLoading) {
      localStorage.setItem('savidna-patients', JSON.stringify(patients));
    }
  }, [patients, useSupabase, isLoading]);
  useEffect(() => {
    if (!useSupabase && !isLoading) {
      localStorage.setItem('savidna-doctors', JSON.stringify(doctors));
    }
  }, [doctors, useSupabase, isLoading]);
  useEffect(() => {
    if (!useSupabase && !isLoading) {
      localStorage.setItem('savidna-appointments', JSON.stringify(appointments));
    }
  }, [appointments, useSupabase, isLoading]);
  useEffect(() => {
    if (!useSupabase && !isLoading) {
      localStorage.setItem('savidna-services', JSON.stringify(services));
    }
  }, [services, useSupabase, isLoading]);
  useEffect(() => {
    if (!useSupabase && !isLoading) {
      localStorage.setItem('savidna-insurances', JSON.stringify(insurances));
    }
  }, [insurances, useSupabase, isLoading]);
  useEffect(() => {
    if (!useSupabase && !isLoading) {
      localStorage.setItem('savidna-specialties', JSON.stringify(specialties));
    }
  }, [specialties, useSupabase, isLoading]);
  useEffect(() => {
    if (!useSupabase && !isLoading) {
      localStorage.setItem('savidna-contacts', JSON.stringify(contacts));
    }
  }, [contacts, useSupabase, isLoading]);
  useEffect(() => {
    if (!useSupabase && !isLoading) {
      localStorage.setItem('savidna-companies', JSON.stringify(companies));
    }
  }, [companies, useSupabase, isLoading]);
  useEffect(() => {
    if (!useSupabase && !isLoading) {
      localStorage.setItem('savidna-opportunities', JSON.stringify(opportunities));
    }
  }, [opportunities, useSupabase, isLoading]);
  useEffect(() => {
    if (!useSupabase && !isLoading) {
      localStorage.setItem('savidna-clinical-histories', JSON.stringify(clinicalHistories));
    }
  }, [clinicalHistories, useSupabase, isLoading]);
  useEffect(() => {
    if (!useSupabase && !isLoading) {
      localStorage.setItem('savidna-roles', JSON.stringify(roles));
    }
  }, [roles, useSupabase, isLoading]);
  useEffect(() => {
    if (!useSupabase && !isLoading) {
      localStorage.setItem('savidna-permissions', JSON.stringify(permissions));
    }
  }, [permissions, useSupabase, isLoading]);
  const loadAllData = async () => {
    try {
      setIsLoading(true);
      if (useSupabase) {
        // Load from Supabase
        const [patientsData, doctorsData, appointmentsData, servicesData, insurancesData, specialtiesData, contactsData, companiesData, opportunitiesData, clinicalHistoriesData, rolesData, permissionsData] = await Promise.all([supabase.from('patients').select('*'), supabase.from('doctors').select('*'), supabase.from('appointments').select('*'), supabase.from('services').select('*'), supabase.from('insurances').select('*'), supabase.from('specialties').select('*'), supabase.from('contacts').select('*'), supabase.from('companies').select('*'), supabase.from('opportunities').select('*'), supabase.from('clinical_histories').select('*'), supabase.from('roles').select('*'), supabase.from('permissions').select('*')]);
        // Transform and set data
        if (patientsData.data) {
          setPatients(patientsData.data.map(p => ({
            id: p.id,
            name: p.name,
            age: p.age,
            gender: p.gender,
            phone: p.phone,
            email: p.email,
            address: p.address,
            emergencyContact: p.emergency_contact,
            insuranceId: p.insurance_id,
            medicalHistory: p.medical_history || {},
            interactions: p.interactions || [],
            createdAt: new Date(p.created_at)
          })));
        }
        if (doctorsData.data) {
          setDoctors(doctorsData.data.map(d => ({
            id: d.id,
            name: d.name,
            specialtyId: d.specialty_id,
            phone: d.phone,
            email: d.email,
            schedule: d.schedule,
            createdAt: new Date(d.created_at)
          })));
        }
        if (appointmentsData.data) {
          setAppointments(appointmentsData.data.map(a => ({
            id: a.id,
            patientId: a.patient_id,
            doctorId: a.doctor_id,
            serviceId: a.service_id,
            date: a.date,
            time: a.time,
            duration: a.duration,
            status: a.status,
            notes: a.notes || '',
            nextAppointmentDate: a.next_appointment_date,
            nextAppointmentNotes: a.next_appointment_notes,
            cancelReason: a.cancel_reason,
            originalDate: a.original_date,
            originalTime: a.original_time,
            createdAt: new Date(a.created_at),
            updatedAt: new Date(a.updated_at)
          })));
        }
        if (servicesData.data) {
          setServices(servicesData.data);
        }
        if (insurancesData.data) {
          setInsurances(insurancesData.data.map(i => ({
            id: i.id,
            name: i.name,
            company: i.company,
            coverageType: i.coverage_type,
            phone: i.phone,
            email: i.email
          })));
        }
        if (specialtiesData.data) {
          setSpecialties(specialtiesData.data);
        }
        if (contactsData.data) {
          setContacts(contactsData.data.map(c => ({
            id: c.id,
            name: c.name,
            phone: c.phone,
            email: c.email,
            company: c.company,
            position: c.position,
            notes: c.notes || '',
            interactions: c.interactions || [],
            createdAt: new Date(c.created_at)
          })));
        }
        if (companiesData.data) {
          setCompanies(companiesData.data.map(c => ({
            id: c.id,
            name: c.name,
            industry: c.industry,
            size: c.size,
            location: c.location,
            phone: c.phone,
            email: c.email,
            website: c.website,
            notes: c.notes || '',
            createdAt: new Date(c.created_at)
          })));
        }
        if (opportunitiesData.data) {
          setOpportunities(opportunitiesData.data.map(o => ({
            id: o.id,
            name: o.name,
            contactId: o.contact_id,
            companyId: o.company_id,
            company: o.company,
            industry: o.industry,
            size: o.size,
            location: o.location,
            stage: o.stage,
            amount: o.amount,
            probability: o.probability,
            notes: o.notes || '',
            interactions: o.interactions || [],
            createdAt: new Date(o.created_at),
            updatedAt: new Date(o.updated_at)
          })));
        }
        if (clinicalHistoriesData.data) {
          setClinicalHistories(clinicalHistoriesData.data.map(ch => ({
            id: ch.id,
            patientId: ch.patient_id,
            date: new Date(ch.date),
            doctorId: ch.doctor_id,
            doctorName: ch.doctor_name,
            personalBackground: ch.personal_background,
            clinicalBackground: ch.clinical_background,
            diagnosis: ch.diagnosis,
            treatment: ch.treatment,
            medicalIndications: ch.medical_indications || '',
            sessions: ch.sessions || '',
            sessionNumber: ch.session_number || '',
            comments: ch.comments || '',
            doctorSignature: ch.doctor_signature || '',
            createdAt: new Date(ch.created_at),
            updatedAt: new Date(ch.updated_at)
          })));
        }
        if (rolesData.data) {
          setRoles(rolesData.data.map(r => ({
            id: r.id,
            name: r.name,
            displayName: r.display_name,
            description: r.description || '',
            isSystemRole: r.is_system_role,
            createdAt: new Date(r.created_at),
            updatedAt: new Date(r.updated_at)
          })));
        }
        if (permissionsData.data) {
          setPermissions(permissionsData.data.map(p => ({
            id: p.id,
            roleId: p.role_id,
            module: p.module,
            canCreate: p.can_create,
            canRead: p.can_read,
            canUpdate: p.can_update,
            canDelete: p.can_delete,
            permissionLevel: p.permission_level,
            createdAt: new Date(p.created_at),
            updatedAt: new Date(p.updated_at)
          })));
        }
      } else {
        // Load from localStorage (demo mode)
        const loadFromStorage = <T,>(key: string, defaultValue: T[]): T[] => {
          const saved = localStorage.getItem(key);
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              // Return parsed data if it's not empty, otherwise use default
              return parsed.length > 0 ? parsed : defaultValue;
            } catch (e) {
              console.error(`Error parsing ${key}:`, e);
              return defaultValue;
            }
          }
          return defaultValue;
        };
        // Load initial data
        const initialData = getInitialData();
        const loadedPatients = loadFromStorage('savidna-patients', initialData.patients);
        const loadedDoctors = loadFromStorage('savidna-doctors', initialData.doctors);
        const loadedAppointments = loadFromStorage('savidna-appointments', initialData.appointments);
        const loadedServices = loadFromStorage('savidna-services', initialData.services);
        const loadedInsurances = loadFromStorage('savidna-insurances', initialData.insurances);
        const loadedSpecialties = loadFromStorage('savidna-specialties', initialData.specialties);
        const loadedContacts = loadFromStorage('savidna-contacts', initialData.contacts);
        const loadedCompanies = loadFromStorage('savidna-companies', initialData.companies);
        const loadedOpportunities = loadFromStorage('savidna-opportunities', initialData.opportunities);
        const loadedClinicalHistories = loadFromStorage('savidna-clinical-histories', []);
        const loadedRoles = loadFromStorage('savidna-roles', []);
        const loadedPermissions = loadFromStorage('savidna-permissions', []);
        setPatients(loadedPatients);
        setDoctors(loadedDoctors);
        setAppointments(loadedAppointments);
        setServices(loadedServices);
        setInsurances(loadedInsurances);
        setSpecialties(loadedSpecialties);
        setContacts(loadedContacts);
        setCompanies(loadedCompanies);
        setOpportunities(loadedOpportunities);
        setClinicalHistories(loadedClinicalHistories);
        setRoles(loadedRoles);
        setPermissions(loadedPermissions);
        // Save initial data to localStorage if it wasn't there
        if (!localStorage.getItem('savidna-patients')) {
          localStorage.setItem('savidna-patients', JSON.stringify(loadedPatients));
        }
        if (!localStorage.getItem('savidna-doctors')) {
          localStorage.setItem('savidna-doctors', JSON.stringify(loadedDoctors));
        }
        if (!localStorage.getItem('savidna-appointments')) {
          localStorage.setItem('savidna-appointments', JSON.stringify(loadedAppointments));
        }
        if (!localStorage.getItem('savidna-services')) {
          localStorage.setItem('savidna-services', JSON.stringify(loadedServices));
        }
        if (!localStorage.getItem('savidna-insurances')) {
          localStorage.setItem('savidna-insurances', JSON.stringify(loadedInsurances));
        }
        if (!localStorage.getItem('savidna-specialties')) {
          localStorage.setItem('savidna-specialties', JSON.stringify(loadedSpecialties));
        }
        if (!localStorage.getItem('savidna-contacts')) {
          localStorage.setItem('savidna-contacts', JSON.stringify(loadedContacts));
        }
        if (!localStorage.getItem('savidna-companies')) {
          localStorage.setItem('savidna-companies', JSON.stringify(loadedCompanies));
        }
        if (!localStorage.getItem('savidna-opportunities')) {
          localStorage.setItem('savidna-opportunities', JSON.stringify(loadedOpportunities));
        }
        if (!localStorage.getItem('savidna-clinical-histories')) {
          localStorage.setItem('savidna-clinical-histories', JSON.stringify(loadedClinicalHistories));
        }
        if (!localStorage.getItem('savidna-roles')) {
          localStorage.setItem('savidna-roles', JSON.stringify(loadedRoles));
        }
        if (!localStorage.getItem('savidna-permissions')) {
          localStorage.setItem('savidna-permissions', JSON.stringify(loadedPermissions));
        }
        console.log('✅ Datos demo cargados:', {
          patients: loadedPatients.length,
          doctors: loadedDoctors.length,
          appointments: loadedAppointments.length,
          services: loadedServices.length,
          insurances: loadedInsurances.length,
          specialties: loadedSpecialties.length
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  // PATIENTS
  const addPatient = async (patientData: Omit<Patient, 'id' | 'medicalHistory' | 'interactions' | 'createdAt'>): Promise<Patient> => {
    if (useSupabase) {
      const {
        data,
        error
      } = await supabase.from('patients').insert({
        name: patientData.name,
        age: patientData.age,
        gender: patientData.gender,
        phone: patientData.phone,
        email: patientData.email,
        address: patientData.address,
        emergency_contact: patientData.emergencyContact,
        insurance_id: patientData.insuranceId,
        medical_history: {},
        interactions: []
      }).select().single();
      if (error) throw error;
      const newPatient: Patient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        phone: data.phone,
        email: data.email,
        address: data.address,
        emergencyContact: data.emergency_contact,
        insuranceId: data.insurance_id,
        medicalHistory: data.medical_history || {},
        interactions: data.interactions || [],
        createdAt: new Date(data.created_at)
      };
      setPatients(prev => [...prev, newPatient]);
      return newPatient;
    } else {
      // localStorage mode
      const newPatient: Patient = {
        ...patientData,
        id: crypto.randomUUID(),
        medicalHistory: {},
        interactions: [],
        createdAt: new Date()
      };
      setPatients(prev => [...prev, newPatient]);
      return newPatient;
    }
  };
  const updatePatient = async (id: string, updates: Partial<Patient>) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('patients').update({
        name: updates.name,
        age: updates.age,
        gender: updates.gender,
        phone: updates.phone,
        email: updates.email,
        address: updates.address,
        emergency_contact: updates.emergencyContact,
        insurance_id: updates.insuranceId,
        medical_history: updates.medicalHistory,
        interactions: updates.interactions
      }).eq('id', id);
      if (error) throw error;
    }
    setPatients(prev => prev.map(p => p.id === id ? {
      ...p,
      ...updates
    } : p));
  };
  const deletePatient = async (id: string) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('patients').delete().eq('id', id);
      if (error) throw error;
    }
    setPatients(prev => prev.filter(p => p.id !== id));
  };
  const getPatient = (id: string) => patients.find(p => p.id === id);
  const updateMedicalHistory = async (patientId: string, history: Partial<MedicalHistory>) => {
    const patient = patients.find(p => p.id === patientId);
    if (!patient) return;
    const updatedHistory = {
      ...patient.medicalHistory,
      ...history
    };
    await updatePatient(patientId, {
      medicalHistory: updatedHistory
    });
  };
  const addMedicalEntry = async (patientId: string, entry: Omit<MedicalEntry, 'id' | 'date'>) => {
    const patient = patients.find(p => p.id === patientId);
    if (!patient) return;
    const newEntry: MedicalEntry = {
      ...entry,
      id: crypto.randomUUID(),
      date: new Date()
    };
    const updatedHistory = {
      ...patient.medicalHistory,
      entries: [...(patient.medicalHistory.entries || []), newEntry]
    };
    await updatePatient(patientId, {
      medicalHistory: updatedHistory
    });
  };
  const addPatientInteraction = async (patientId: string, interaction: Omit<Interaction, 'id' | 'date'>) => {
    const patient = patients.find(p => p.id === patientId);
    if (!patient) return;
    const newInteraction: Interaction = {
      ...interaction,
      id: crypto.randomUUID(),
      date: new Date()
    };
    await updatePatient(patientId, {
      interactions: [...patient.interactions, newInteraction]
    });
  };
  // DOCTORS
  const addDoctor = async (doctorData: Omit<Doctor, 'id' | 'createdAt'>): Promise<Doctor> => {
    if (useSupabase) {
      const {
        data,
        error
      } = await supabase.from('doctors').insert({
        name: doctorData.name,
        specialty_id: doctorData.specialtyId,
        phone: doctorData.phone,
        email: doctorData.email,
        schedule: doctorData.schedule
      }).select().single();
      if (error) throw error;
      const newDoctor: Doctor = {
        id: data.id,
        name: data.name,
        specialtyId: data.specialty_id,
        phone: data.phone,
        email: data.email,
        schedule: data.schedule,
        createdAt: new Date(data.created_at)
      };
      setDoctors(prev => [...prev, newDoctor]);
      return newDoctor;
    } else {
      const newDoctor: Doctor = {
        ...doctorData,
        id: crypto.randomUUID(),
        createdAt: new Date()
      };
      setDoctors(prev => [...prev, newDoctor]);
      return newDoctor;
    }
  };
  const updateDoctor = async (id: string, updates: Partial<Doctor>) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('doctors').update({
        name: updates.name,
        specialty_id: updates.specialtyId,
        phone: updates.phone,
        email: updates.email,
        schedule: updates.schedule
      }).eq('id', id);
      if (error) throw error;
    }
    setDoctors(prev => prev.map(d => d.id === id ? {
      ...d,
      ...updates
    } : d));
  };
  const deleteDoctor = async (id: string) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('doctors').delete().eq('id', id);
      if (error) throw error;
    }
    setDoctors(prev => prev.filter(d => d.id !== id));
  };
  const getDoctor = (id: string) => doctors.find(d => d.id === id);
  const getDoctorsBySpecialty = (specialtyId: string) => doctors.filter(d => d.specialtyId === specialtyId);
  // APPOINTMENTS
  const addAppointment = async (appointmentData: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>): Promise<Appointment> => {
    if (useSupabase) {
      const {
        data,
        error
      } = await supabase.from('appointments').insert({
        patient_id: appointmentData.patientId,
        doctor_id: appointmentData.doctorId,
        service_id: appointmentData.serviceId,
        date: appointmentData.date,
        time: appointmentData.time,
        duration: appointmentData.duration,
        status: appointmentData.status,
        notes: appointmentData.notes
      }).select().single();
      if (error) throw error;
      const newAppointment: Appointment = {
        id: data.id,
        patientId: data.patient_id,
        doctorId: data.doctor_id,
        serviceId: data.service_id,
        date: data.date,
        time: data.time,
        duration: data.duration,
        status: data.status,
        notes: data.notes || '',
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };
      setAppointments(prev => [...prev, newAppointment]);
      // Send email notification
      const patient = getPatient(appointmentData.patientId);
      const doctor = getDoctor(appointmentData.doctorId);
      const service = getService(appointmentData.serviceId);
      if (patient && doctor && service && emailNotificationService.isEnabled()) {
        await emailNotificationService.sendAppointmentCreated({
          patientName: patient.name,
          patientEmail: patient.email,
          doctorName: doctor.name,
          serviceName: service.name,
          date: appointmentData.date,
          time: appointmentData.time,
          duration: appointmentData.duration,
          notes: appointmentData.notes
        });
      }
      return newAppointment;
    } else {
      const newAppointment: Appointment = {
        ...appointmentData,
        id: crypto.randomUUID(),
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setAppointments(prev => [...prev, newAppointment]);
      return newAppointment;
    }
  };
  const updateAppointment = async (id: string, updates: Partial<Appointment>) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('appointments').update({
        patient_id: updates.patientId,
        doctor_id: updates.doctorId,
        service_id: updates.serviceId,
        date: updates.date,
        time: updates.time,
        duration: updates.duration,
        status: updates.status,
        notes: updates.notes,
        next_appointment_date: updates.nextAppointmentDate,
        next_appointment_notes: updates.nextAppointmentNotes,
        cancel_reason: updates.cancelReason,
        original_date: updates.originalDate,
        original_time: updates.originalTime
      }).eq('id', id);
      if (error) throw error;
    }
    setAppointments(prev => prev.map(a => a.id === id ? {
      ...a,
      ...updates,
      updatedAt: new Date()
    } : a));
  };
  const cancelAppointment = async (id: string, reason: string) => {
    await updateAppointment(id, {
      status: 'cancelada',
      cancelReason: reason
    });
  };
  const rescheduleAppointment = async (id: string, newDate: string, newTime: string) => {
    const appointment = appointments.find(a => a.id === id);
    if (!appointment) return;
    await updateAppointment(id, {
      date: newDate,
      time: newTime,
      status: 'reprogramada',
      originalDate: appointment.date,
      originalTime: appointment.time
    });
  };
  const completeAppointment = async (id: string, nextAppointmentDate?: string, nextAppointmentNotes?: string) => {
    await updateAppointment(id, {
      status: 'completada',
      nextAppointmentDate,
      nextAppointmentNotes
    });
    if (nextAppointmentDate) {
      const appointment = appointments.find(a => a.id === id);
      if (appointment) {
        await addAppointment({
          patientId: appointment.patientId,
          doctorId: appointment.doctorId,
          serviceId: appointment.serviceId,
          date: nextAppointmentDate,
          time: appointment.time,
          duration: appointment.duration,
          status: 'programada',
          notes: nextAppointmentNotes || 'Cita de seguimiento'
        });
      }
    }
  };
  const getAppointment = (id: string) => appointments.find(a => a.id === id);
  const getAppointmentsByDate = (date: string) => appointments.filter(a => a.date === date);
  const getAppointmentsByPatient = (patientId: string) => appointments.filter(a => a.patientId === patientId);
  const getAppointmentsByDoctor = (doctorId: string) => appointments.filter(a => a.doctorId === doctorId);
  const checkDoctorAvailability = (doctorId: string, date: string, time: string, excludeId?: string): boolean => {
    const existingAppointments = appointments.filter(a => a.doctorId === doctorId && a.date === date && a.status !== 'cancelada' && a.id !== excludeId);
    const requestedTime = parseInt(time.replace(':', ''));
    for (const apt of existingAppointments) {
      const aptStart = parseInt(apt.time.replace(':', ''));
      const aptEnd = aptStart + Math.floor(apt.duration / 60) * 100 + apt.duration % 60;
      if (requestedTime >= aptStart && requestedTime < aptEnd) {
        return false;
      }
    }
    return true;
  };
  const syncAllAppointmentsToSheets = async () => {
    console.log('Google Sheets sync deprecated - using Supabase or localStorage');
  };
  // SERVICES
  const addService = async (serviceData: Omit<Service, 'id'>): Promise<Service> => {
    if (useSupabase) {
      const {
        data,
        error
      } = await supabase.from('services').insert(serviceData).select().single();
      if (error) throw error;
      setServices(prev => [...prev, data]);
      return data;
    } else {
      const newService: Service = {
        ...serviceData,
        id: crypto.randomUUID()
      };
      setServices(prev => [...prev, newService]);
      return newService;
    }
  };
  const updateService = async (id: string, updates: Partial<Service>) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('services').update(updates).eq('id', id);
      if (error) throw error;
    }
    setServices(prev => prev.map(s => s.id === id ? {
      ...s,
      ...updates
    } : s));
  };
  const deleteService = async (id: string) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('services').delete().eq('id', id);
      if (error) throw error;
    }
    setServices(prev => prev.filter(s => s.id !== id));
  };
  const getService = (id: string) => services.find(s => s.id === id);
  // INSURANCES
  const addInsurance = async (insuranceData: Omit<Insurance, 'id'>): Promise<Insurance> => {
    if (useSupabase) {
      const {
        data,
        error
      } = await supabase.from('insurances').insert({
        name: insuranceData.name,
        company: insuranceData.company,
        coverage_type: insuranceData.coverageType,
        phone: insuranceData.phone,
        email: insuranceData.email
      }).select().single();
      if (error) throw error;
      const newInsurance: Insurance = {
        id: data.id,
        name: data.name,
        company: data.company,
        coverageType: data.coverage_type,
        phone: data.phone,
        email: data.email
      };
      setInsurances(prev => [...prev, newInsurance]);
      return newInsurance;
    } else {
      const newInsurance: Insurance = {
        ...insuranceData,
        id: crypto.randomUUID()
      };
      setInsurances(prev => [...prev, newInsurance]);
      return newInsurance;
    }
  };
  const updateInsurance = async (id: string, updates: Partial<Insurance>) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('insurances').update({
        name: updates.name,
        company: updates.company,
        coverage_type: updates.coverageType,
        phone: updates.phone,
        email: updates.email
      }).eq('id', id);
      if (error) throw error;
    }
    setInsurances(prev => prev.map(i => i.id === id ? {
      ...i,
      ...updates
    } : i));
  };
  const deleteInsurance = async (id: string) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('insurances').delete().eq('id', id);
      if (error) throw error;
    }
    setInsurances(prev => prev.filter(i => i.id !== id));
  };
  const getInsurance = (id: string) => insurances.find(i => i.id === id);
  // SPECIALTIES
  const addSpecialty = async (specialtyData: Omit<Specialty, 'id'>): Promise<Specialty> => {
    if (useSupabase) {
      const {
        data,
        error
      } = await supabase.from('specialties').insert(specialtyData).select().single();
      if (error) throw error;
      setSpecialties(prev => [...prev, data]);
      return data;
    } else {
      const newSpecialty: Specialty = {
        ...specialtyData,
        id: crypto.randomUUID()
      };
      setSpecialties(prev => [...prev, newSpecialty]);
      return newSpecialty;
    }
  };
  const updateSpecialty = async (id: string, updates: Partial<Specialty>) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('specialties').update(updates).eq('id', id);
      if (error) throw error;
    }
    setSpecialties(prev => prev.map(s => s.id === id ? {
      ...s,
      ...updates
    } : s));
  };
  const deleteSpecialty = async (id: string) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('specialties').delete().eq('id', id);
      if (error) throw error;
    }
    setSpecialties(prev => prev.filter(s => s.id !== id));
  };
  const getSpecialty = (id: string) => specialties.find(s => s.id === id);
  // CONTACTS
  const addContact = async (contactData: Omit<Contact, 'id' | 'interactions' | 'createdAt'>): Promise<Contact> => {
    if (useSupabase) {
      const {
        data,
        error
      } = await supabase.from('contacts').insert({
        name: contactData.name,
        phone: contactData.phone,
        email: contactData.email,
        company: contactData.company,
        position: contactData.position,
        notes: contactData.notes,
        interactions: []
      }).select().single();
      if (error) throw error;
      const newContact: Contact = {
        id: data.id,
        name: data.name,
        phone: data.phone,
        email: data.email,
        company: data.company,
        position: data.position,
        notes: data.notes || '',
        interactions: data.interactions || [],
        createdAt: new Date(data.created_at)
      };
      setContacts(prev => [...prev, newContact]);
      return newContact;
    } else {
      const newContact: Contact = {
        ...contactData,
        id: crypto.randomUUID(),
        interactions: [],
        createdAt: new Date()
      };
      setContacts(prev => [...prev, newContact]);
      return newContact;
    }
  };
  const updateContact = async (id: string, updates: Partial<Contact>) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('contacts').update({
        name: updates.name,
        phone: updates.phone,
        email: updates.email,
        company: updates.company,
        position: updates.position,
        notes: updates.notes,
        interactions: updates.interactions
      }).eq('id', id);
      if (error) throw error;
    }
    setContacts(prev => prev.map(c => c.id === id ? {
      ...c,
      ...updates
    } : c));
  };
  const deleteContact = async (id: string) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('contacts').delete().eq('id', id);
      if (error) throw error;
    }
    setContacts(prev => prev.filter(c => c.id !== id));
  };
  const getContact = (id: string) => contacts.find(c => c.id === id);
  const addContactInteraction = async (contactId: string, interaction: Omit<Interaction, 'id' | 'date'>) => {
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return;
    const newInteraction: Interaction = {
      ...interaction,
      id: crypto.randomUUID(),
      date: new Date()
    };
    await updateContact(contactId, {
      interactions: [...contact.interactions, newInteraction]
    });
  };
  // COMPANIES
  const addCompany = async (companyData: Omit<Company, 'id' | 'createdAt'>): Promise<Company> => {
    if (useSupabase) {
      const {
        data,
        error
      } = await supabase.from('companies').insert({
        name: companyData.name,
        industry: companyData.industry,
        size: companyData.size,
        location: companyData.location,
        phone: companyData.phone,
        email: companyData.email,
        website: companyData.website,
        notes: companyData.notes
      }).select().single();
      if (error) throw error;
      const newCompany: Company = {
        id: data.id,
        name: data.name,
        industry: data.industry,
        size: data.size,
        location: data.location,
        phone: data.phone,
        email: data.email,
        website: data.website,
        notes: data.notes || '',
        createdAt: new Date(data.created_at)
      };
      setCompanies(prev => [...prev, newCompany]);
      return newCompany;
    } else {
      const newCompany: Company = {
        ...companyData,
        id: crypto.randomUUID(),
        createdAt: new Date()
      };
      setCompanies(prev => [...prev, newCompany]);
      return newCompany;
    }
  };
  const updateCompany = async (id: string, updates: Partial<Company>) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('companies').update(updates).eq('id', id);
      if (error) throw error;
    }
    setCompanies(prev => prev.map(c => c.id === id ? {
      ...c,
      ...updates
    } : c));
  };
  const deleteCompany = async (id: string) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('companies').delete().eq('id', id);
      if (error) throw error;
    }
    setCompanies(prev => prev.filter(c => c.id !== id));
  };
  const getCompany = (id: string) => companies.find(c => c.id === id);
  // OPPORTUNITIES
  const addOpportunity = async (opportunityData: Omit<Opportunity, 'id' | 'interactions' | 'createdAt' | 'updatedAt'>): Promise<Opportunity> => {
    if (useSupabase) {
      const {
        data,
        error
      } = await supabase.from('opportunities').insert({
        name: opportunityData.name,
        contact_id: opportunityData.contactId,
        company_id: opportunityData.companyId,
        company: opportunityData.company,
        industry: opportunityData.industry,
        size: opportunityData.size,
        location: opportunityData.location,
        stage: opportunityData.stage,
        amount: opportunityData.amount,
        probability: opportunityData.probability,
        notes: opportunityData.notes,
        interactions: []
      }).select().single();
      if (error) throw error;
      const newOpportunity: Opportunity = {
        id: data.id,
        name: data.name,
        contactId: data.contact_id,
        companyId: data.company_id,
        company: data.company,
        industry: data.industry,
        size: data.size,
        location: data.location,
        stage: data.stage,
        amount: data.amount,
        probability: data.probability,
        notes: data.notes || '',
        interactions: data.interactions || [],
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };
      setOpportunities(prev => [...prev, newOpportunity]);
      return newOpportunity;
    } else {
      const newOpportunity: Opportunity = {
        ...opportunityData,
        id: crypto.randomUUID(),
        interactions: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setOpportunities(prev => [...prev, newOpportunity]);
      return newOpportunity;
    }
  };
  const updateOpportunity = async (id: string, updates: Partial<Opportunity>) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('opportunities').update({
        name: updates.name,
        contact_id: updates.contactId,
        company_id: updates.companyId,
        company: updates.company,
        industry: updates.industry,
        size: updates.size,
        location: updates.location,
        stage: updates.stage,
        amount: updates.amount,
        probability: updates.probability,
        notes: updates.notes,
        interactions: updates.interactions
      }).eq('id', id);
      if (error) throw error;
    }
    setOpportunities(prev => prev.map(o => o.id === id ? {
      ...o,
      ...updates,
      updatedAt: new Date()
    } : o));
  };
  const deleteOpportunity = async (id: string) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('opportunities').delete().eq('id', id);
      if (error) throw error;
    }
    setOpportunities(prev => prev.filter(o => o.id !== id));
  };
  const getOpportunity = (id: string) => opportunities.find(o => o.id === id);
  const addOpportunityInteraction = async (opportunityId: string, interaction: Omit<Interaction, 'id' | 'date'>) => {
    const opportunity = opportunities.find(o => o.id === opportunityId);
    if (!opportunity) return;
    const newInteraction: Interaction = {
      ...interaction,
      id: crypto.randomUUID(),
      date: new Date()
    };
    await updateOpportunity(opportunityId, {
      interactions: [...opportunity.interactions, newInteraction]
    });
  };
  // CLINICAL HISTORIES
  const addClinicalHistory = async (historyData: Omit<ClinicalHistory, 'id' | 'createdAt' | 'updatedAt'>): Promise<ClinicalHistory> => {
    if (useSupabase) {
      const {
        data,
        error
      } = await supabase.from('clinical_histories').insert({
        patient_id: historyData.patientId,
        date: historyData.date,
        doctor_id: historyData.doctorId,
        doctor_name: historyData.doctorName,
        personal_background: historyData.personalBackground,
        clinical_background: historyData.clinicalBackground,
        diagnosis: historyData.diagnosis,
        treatment: historyData.treatment,
        medical_indications: historyData.medicalIndications,
        sessions: historyData.sessions,
        session_number: historyData.sessionNumber,
        comments: historyData.comments,
        doctor_signature: historyData.doctorSignature
      }).select().single();
      if (error) throw error;
      const newHistory: ClinicalHistory = {
        id: data.id,
        patientId: data.patient_id,
        date: new Date(data.date),
        doctorId: data.doctor_id,
        doctorName: data.doctor_name,
        personalBackground: data.personal_background,
        clinicalBackground: data.clinical_background,
        diagnosis: data.diagnosis,
        treatment: data.treatment,
        medicalIndications: data.medical_indications || '',
        sessions: data.sessions || '',
        sessionNumber: data.session_number || '',
        comments: data.comments || '',
        doctorSignature: data.doctor_signature || '',
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };
      setClinicalHistories(prev => [...prev, newHistory]);
      return newHistory;
    } else {
      const newHistory: ClinicalHistory = {
        ...historyData,
        id: crypto.randomUUID(),
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setClinicalHistories(prev => [...prev, newHistory]);
      return newHistory;
    }
  };
  const updateClinicalHistory = async (id: string, updates: Partial<ClinicalHistory>) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('clinical_histories').update({
        patient_id: updates.patientId,
        date: updates.date,
        doctor_id: updates.doctorId,
        doctor_name: updates.doctorName,
        personal_background: updates.personalBackground,
        clinical_background: updates.clinicalBackground,
        diagnosis: updates.diagnosis,
        treatment: updates.treatment,
        medical_indications: updates.medicalIndications,
        sessions: updates.sessions,
        session_number: updates.sessionNumber,
        comments: updates.comments,
        doctor_signature: updates.doctorSignature
      }).eq('id', id);
      if (error) throw error;
    }
    setClinicalHistories(prev => prev.map(ch => ch.id === id ? {
      ...ch,
      ...updates,
      updatedAt: new Date()
    } : ch));
  };
  const deleteClinicalHistory = async (id: string) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('clinical_histories').delete().eq('id', id);
      if (error) throw error;
    }
    setClinicalHistories(prev => prev.filter(ch => ch.id !== id));
  };
  const getClinicalHistory = (id: string) => clinicalHistories.find(ch => ch.id === id);
  const getClinicalHistoriesByPatient = (patientId: string) => clinicalHistories.filter(ch => ch.patientId === patientId);
  // ROLES
  const addRole = async (roleData: Omit<Role, 'id' | 'createdAt' | 'updatedAt'>): Promise<Role> => {
    if (useSupabase) {
      const {
        data,
        error
      } = await supabase.from('roles').insert({
        name: roleData.name,
        display_name: roleData.displayName,
        description: roleData.description,
        is_system_role: roleData.isSystemRole
      }).select().single();
      if (error) throw error;
      const newRole: Role = {
        id: data.id,
        name: data.name,
        displayName: data.display_name,
        description: data.description || '',
        isSystemRole: data.is_system_role,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };
      setRoles(prev => [...prev, newRole]);
      return newRole;
    } else {
      const newRole: Role = {
        ...roleData,
        id: crypto.randomUUID(),
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setRoles(prev => [...prev, newRole]);
      localStorage.setItem('savidna-roles', JSON.stringify([...roles, newRole]));
      return newRole;
    }
  };
  const updateRole = async (id: string, updates: Partial<Role>) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('roles').update({
        display_name: updates.displayName,
        description: updates.description
      }).eq('id', id);
      if (error) throw error;
    }
    setRoles(prev => prev.map(r => r.id === id ? {
      ...r,
      ...updates,
      updatedAt: new Date()
    } : r));
    if (!useSupabase) {
      const updatedRoles = roles.map(r => r.id === id ? {
        ...r,
        ...updates,
        updatedAt: new Date()
      } : r);
      localStorage.setItem('savidna-roles', JSON.stringify(updatedRoles));
    }
  };
  const deleteRole = async (id: string) => {
    if (useSupabase) {
      const {
        error
      } = await supabase.from('roles').delete().eq('id', id);
      if (error) throw error;
    }
    setRoles(prev => prev.filter(r => r.id !== id));
    setPermissions(prev => prev.filter(p => p.roleId !== id));
    if (!useSupabase) {
      const updatedRoles = roles.filter(r => r.id !== id);
      const updatedPermissions = permissions.filter(p => p.roleId !== id);
      localStorage.setItem('savidna-roles', JSON.stringify(updatedRoles));
      localStorage.setItem('savidna-permissions', JSON.stringify(updatedPermissions));
    }
  };
  const getRole = (id: string) => roles.find(r => r.id === id);
  // PERMISSIONS
  const getPermissionsByRole = (roleId: string) => permissions.filter(p => p.roleId === roleId);
  const updatePermissions = async (roleId: string, updatedPermissions: Permission[]) => {
    if (useSupabase) {
      // Update each permission
      const updates = updatedPermissions.map(p => supabase.from('permissions').update({
        can_create: p.canCreate,
        can_read: p.canRead,
        can_update: p.canUpdate,
        can_delete: p.canDelete,
        permission_level: p.permissionLevel
      }).eq('id', p.id));
      await Promise.all(updates);
    }
    // Update local state
    setPermissions(prev => prev.map(p => {
      const updated = updatedPermissions.find(up => up.id === p.id);
      return updated ? {
        ...updated,
        updatedAt: new Date()
      } : p;
    }));
    if (!useSupabase) {
      const allPermissions = permissions.map(p => {
        const updated = updatedPermissions.find(up => up.id === p.id);
        return updated ? {
          ...updated,
          updatedAt: new Date()
        } : p;
      });
      localStorage.setItem('savidna-permissions', JSON.stringify(allPermissions));
    }
  };
  // STATS
  const getTodayAppointmentsCount = () => {
    const today = getToday();
    return appointments.filter(a => a.date === today && a.status !== 'cancelada').length;
  };
  const getTotalOpportunitiesAmount = () => {
    return opportunities.reduce((sum, o) => sum + o.amount, 0);
  };
  return <DataContext.Provider value={{
    patients,
    addPatient,
    updatePatient,
    deletePatient,
    getPatient,
    updateMedicalHistory,
    addMedicalEntry,
    addPatientInteraction,
    doctors,
    addDoctor,
    updateDoctor,
    deleteDoctor,
    getDoctor,
    getDoctorsBySpecialty,
    appointments,
    addAppointment,
    updateAppointment,
    cancelAppointment,
    rescheduleAppointment,
    completeAppointment,
    getAppointment,
    getAppointmentsByDate,
    getAppointmentsByPatient,
    getAppointmentsByDoctor,
    checkDoctorAvailability,
    syncAllAppointmentsToSheets,
    services,
    addService,
    updateService,
    deleteService,
    getService,
    insurances,
    addInsurance,
    updateInsurance,
    deleteInsurance,
    getInsurance,
    specialties,
    addSpecialty,
    updateSpecialty,
    deleteSpecialty,
    getSpecialty,
    contacts,
    addContact,
    updateContact,
    deleteContact,
    getContact,
    addContactInteraction,
    companies,
    addCompany,
    updateCompany,
    deleteCompany,
    getCompany,
    opportunities,
    addOpportunity,
    updateOpportunity,
    deleteOpportunity,
    getOpportunity,
    addOpportunityInteraction,
    clinicalHistories,
    addClinicalHistory,
    updateClinicalHistory,
    deleteClinicalHistory,
    getClinicalHistory,
    getClinicalHistoriesByPatient,
    roles,
    addRole,
    updateRole,
    deleteRole,
    getRole,
    permissions,
    getPermissionsByRole,
    updatePermissions,
    getTodayAppointmentsCount,
    getTotalOpportunitiesAmount,
    isLoading
  }} data-id="element-1793">
      {children}
    </DataContext.Provider>;
}
export function useData(): DataContextType {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}